import React, { useCallback, useRef, useState } from "react";
import style from "../scss/Header.module.scss";
import { Icon } from "@iconify/react";
import {
  RUTA_LOGIN_SUPERADMIN,
  cerrarSesion,
  esAdmin,
  esSuperadmin,
  obtenerRol,
  obtenerUsername,
  rutaInicio,
  rutaLogin,
} from "../utils/sesion";
import { etiquetaRol } from "../constants/roles";
import RecordatoriosAgendaComponent from "./RecordatoriosAgendaComponent";
import useCerrarAlClicFuera from "../hooks/useCerrarAlClicFuera";

const URL_DONDE_VOTAR = "https://eleccionescolombia.registraduria.gov.co/identificacion";

// enlaces del menú: se reutilizan en desktop (texto corto) y en móvil (título completo).
// enMenuUsuario: en desktop van en el menú del usuario (no en la barra) para que todo quepa.
const ENLACES = [
  { href: "/consulta", texto: "Consulta", titulo: "Consulta de electores", icono: "mdi:home-search-outline" },
  { href: "/registro", texto: "Registro", titulo: "Registro de electores", icono: "mdi:account-plus-outline" },
  { href: URL_DONDE_VOTAR, texto: "¿Dónde votar?", titulo: "¿Dónde votar?", icono: "mdi:map-marker-radius-outline", externo: true },
  { href: "/conteo", texto: "Escrutinio", titulo: "Registro de votación", icono: "mdi:vote-outline" },
  { href: "/agenda", texto: "Agenda", titulo: "Mi agenda", icono: "mdi:calendar-check-outline" },
  // el digitador no ve Catálogos ni Usuarios (el backend también lo restringe)
  { href: "/catalogos", texto: "Catálogos", titulo: "Catálogos", icono: "mdi:format-list-bulleted-square", soloAdmin: true, enMenuUsuario: true },
  { href: "/usuarios", texto: "Usuarios", titulo: "Usuarios", icono: "mdi:account-group-outline", soloAdmin: true, enMenuUsuario: true },
  // el superadmin solo gestiona los administradores (no ve los datos de las campañas)
  { href: "/administradores", texto: "Administradores", titulo: "Administradores", icono: "mdi:shield-account-outline", soloSuperadmin: true },
];

// enlaces que ve cada rol: el superadmin solo los suyos; admin y digitador el resto
const enlacesDelRol = (superadmin, admin) =>
  ENLACES.filter((enlace) => (superadmin ? enlace.soloSuperadmin : !enlace.soloSuperadmin && (admin || !enlace.soloAdmin)));

// perfil del usuario en sesión: va en el menú del usuario (desktop) y al final del menú móvil
const ENLACE_PERFIL = { href: "/perfil", texto: "Mi perfil", titulo: "Mi perfil", icono: "mdi:account-cog-outline" };

const HeaderComponent = () => {
  const [menu, setMenu] = useState(false);
  const [menuUsuario, setMenuUsuario] = useState(false);
  const menuUsuarioRef = useRef(null);
  const admin = esAdmin();
  const superadmin = esSuperadmin();
  const username = obtenerUsername();
  const rol = obtenerRol();
  const rutaActual = window.location.pathname;
  const rutaSalir = rutaLogin(rol);

  const cerrarMenuUsuario = useCallback(() => setMenuUsuario(false), []);
  useCerrarAlClicFuera(menuUsuarioRef, menuUsuario, cerrarMenuUsuario);

  const enlaces = enlacesDelRol(superadmin, admin);
  const enlacesBarra = enlaces.filter((enlace) => !enlace.enMenuUsuario);
  const enlacesMenuUsuario = enlaces.filter((enlace) => enlace.enMenuUsuario);

  const esActivo = (enlace) => !enlace.externo && rutaActual.startsWith(enlace.href);
  const clasesEnlace = (enlace, base) => `${base} ${esActivo(enlace) ? style.activo : ""}`;

  const propsExterno = (enlace) => (enlace.externo ? { target: "_blank", rel: "noreferrer" } : {});

  if (rutaActual === "/" || rutaActual === RUTA_LOGIN_SUPERADMIN) {
    return (
      <header className={style.headerlogin}>
        <Icon icon="mdi:shield-check-outline" aria-hidden="true" />
        <p className="texto-en-negrilla">¡Mi gente firme!</p>
      </header>
    );
  }

  return (
    <header className={`${style.header} mb-5`}>
      <a href={rutaInicio(rol)} className={style.marca}>
        <img src="../../Img/logoElecciones.png" className={style.marcaLogo} alt="logo" />
        <span className={style.marcaTexto}>
          <strong>Control Electoral</strong>
          <small>¡Mi gente firme!</small>
        </span>
      </a>

      <nav className={style.navDesktop} aria-label="Menú principal">
        {enlacesBarra.map((enlace) => (
          <a
            key={enlace.href}
            href={enlace.href}
            title={enlace.titulo}
            aria-current={esActivo(enlace) ? "page" : undefined}
            className={clasesEnlace(enlace, style.navItem)}
            {...propsExterno(enlace)}
          >
            <Icon icon={enlace.icono} className={style.navIcono} aria-hidden="true" />
            <span className={style.navTexto}>{enlace.texto}</span>
          </a>
        ))}
      </nav>

      <div className={style.acciones}>
        {/* recordatorios de la agenda personal (solo con sesión abierta; el superadmin no tiene agenda) */}
        {username && !superadmin && <RecordatoriosAgendaComponent />}

        {/* usuario: en desktop abre el menú con administración y cerrar sesión */}
        {username && (
          <div className={style.usuarioHeader} ref={menuUsuarioRef}>
            <button
              type="button"
              className={`${style.usuarioSesion} ${menuUsuario ? style.usuarioSesionAbierto : ""}`}
              onClick={() => setMenuUsuario(!menuUsuario)}
              aria-haspopup="menu"
              aria-expanded={menuUsuario}
              title={`Sesión iniciada como ${username}`}
            >
              <span className={style.usuarioAvatar} aria-hidden="true">
                {username.charAt(0).toUpperCase()}
              </span>
              <span className={style.usuarioTexto}>
                <span className={style.usuarioNombre}>{username}</span>
                {rol && <span className={style.usuarioRol}>{etiquetaRol(rol)}</span>}
              </span>
              <Icon
                icon="mdi:chevron-down"
                className={`${style.usuarioFlecha} ${menuUsuario ? style.usuarioFlechaAbierta : ""}`}
                aria-hidden="true"
              />
            </button>

            {menuUsuario && (
              <div className={style.menuUsuario} role="menu">
                <div className={style.menuUsuarioCabecera}>
                  <strong>{username}</strong>
                  {rol && <span>{etiquetaRol(rol)}</span>}
                </div>
                <a
                  href={ENLACE_PERFIL.href}
                  role="menuitem"
                  className={clasesEnlace(ENLACE_PERFIL, style.menuUsuarioItem)}
                >
                  <Icon icon={ENLACE_PERFIL.icono} aria-hidden="true" />
                  {ENLACE_PERFIL.titulo}
                </a>
                {enlacesMenuUsuario.length > 0 && (
                  <div className={style.menuUsuarioGrupo}>
                    <span className={style.menuUsuarioTitulo}>Administración</span>
                    {enlacesMenuUsuario.map((enlace) => (
                      <a
                        key={enlace.href}
                        href={enlace.href}
                        role="menuitem"
                        className={clasesEnlace(enlace, style.menuUsuarioItem)}
                      >
                        <Icon icon={enlace.icono} aria-hidden="true" />
                        {enlace.titulo}
                      </a>
                    ))}
                  </div>
                )}
                <a
                  href={rutaSalir}
                  role="menuitem"
                  className={`${style.menuUsuarioItem} ${style.menuUsuarioSalir}`}
                  onClick={cerrarSesion}
                >
                  <Icon icon="mdi:logout" aria-hidden="true" />
                  Cerrar sesión
                </a>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          className={style.btnMenu}
          aria-label={menu ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={menu}
          onClick={() => setMenu(!menu)}
        >
          <Icon icon={menu ? "mdi:close" : "mdi:menu"} />
        </button>
      </div>

      <nav className={`${style.navMovil} ${menu ? style.active : ""}`} aria-label="Menú principal móvil">
        {enlaces.map((enlace) => (
          <a
            key={enlace.href}
            href={enlace.href}
            className={clasesEnlace(enlace, style.navMovilItem)}
            {...propsExterno(enlace)}
          >
            <Icon icon={enlace.icono} className={style.navIcono} aria-hidden="true" />
            {enlace.titulo}
            {enlace.externo && <Icon icon="mdi:open-in-new" className={style.iconoExterno} aria-hidden="true" />}
          </a>
        ))}

        <a href={ENLACE_PERFIL.href} className={clasesEnlace(ENLACE_PERFIL, style.navMovilItem)}>
          <Icon icon={ENLACE_PERFIL.icono} className={style.navIcono} aria-hidden="true" />
          {ENLACE_PERFIL.titulo}
        </a>

        <a href={rutaSalir} className={`${style.navMovilItem} ${style.navMovilSalir}`} onClick={cerrarSesion}>
          <Icon icon="mdi:logout" className={style.navIcono} aria-hidden="true" />
          Cerrar sesión
        </a>
      </nav>
    </header>
  );
};
export default HeaderComponent;
